import cx from 'clsx';
import { RemoveScroll } from 'react-remove-scroll';
import { useId } from '@mantine/hooks';
import {
  Box,
  BoxProps,
  ElementProps,
  factory,
  Factory,
  StylesApiProps,
  useProps,
} from '../../../core';
import { useAppShellContext } from '../AppShell.context';
import { AppShellCompoundProps } from '../AppShell.types';
import { AppShellResizeHandle } from '../AppShellResizeHandle/AppShellResizeHandle';
import classes from '../AppShell.module.css';

export type AppShellAsideStylesNames = 'aside';

export interface AppShellAsideProps
  extends
    BoxProps,
    AppShellCompoundProps,
    StylesApiProps<AppShellAsideFactory>,
    ElementProps<'aside'> {}

export type AppShellAsideFactory = Factory<{
  props: AppShellAsideProps;
  ref: HTMLElement;
  stylesNames: AppShellAsideStylesNames;
}>;

export const AppShellAside = factory<AppShellAsideFactory>((_props) => {
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    withBorder,
    zIndex,
    mod,
    ...others
  } = useProps('AppShellAside', null, _props);

  const ctx = useAppShellContext();
  const sectionId = useId(others.id);

  if (ctx.disabled) {
    return null;
  }

  const isResizable = ctx.resize?.aside.enabled ?? false;
  const boxProps = ctx.getStyles('aside', {
    className: cx({ [RemoveScroll.classNames.zeroRight]: ctx.offsetScrollbars }, className),
    classNames,
    styles,
    style,
  });

  if (!isResizable) {
    return (
      <Box
        component="aside"
        mod={[{ 'with-border': withBorder ?? ctx.withBorder }, mod]}
        {...boxProps}
        {...others}
        __vars={{ '--app-shell-aside-z-index': `calc(${zIndex ?? ctx.zIndex} + 1)` }}
      />
    );
  }

  const { children, dangerouslySetInnerHTML, ...rest } = others;

  return (
    <Box
      component="aside"
      mod={[{ 'with-border': withBorder ?? ctx.withBorder }, mod]}
      {...boxProps}
      {...rest}
      __vars={{ '--app-shell-aside-z-index': `calc(${zIndex ?? ctx.zIndex} + 1)` }}
      id={sectionId}
    >
      {dangerouslySetInnerHTML ? (
        <div style={{ display: 'contents' }} dangerouslySetInnerHTML={dangerouslySetInnerHTML} />
      ) : (
        children
      )}
      <AppShellResizeHandle section="aside" sectionId={sectionId} />
    </Box>
  );
});

AppShellAside.classes = classes;
AppShellAside.displayName = '@mantine/core/AppShellAside';
